import {
  useLoaderData,
  useRouteLoaderData,
  useRevalidator,
  useParams,
  useNavigate,
  useSearchParams,
  Outlet,
} from 'react-router-dom'
import { useMemo, useCallback, useState } from 'react'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import EmailIcon from '@mui/icons-material/Email'
import LaunchIcon from '@mui/icons-material/Launch'
import EditIcon from '@mui/icons-material/Edit'
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft'
import DeleteIcon from '@mui/icons-material/Delete'
import LocalActivityIcon from '@mui/icons-material/LocalActivity'
import Divider from '@mui/material/Divider'
import AddBoxIcon from '@mui/icons-material/AddBox'
import { useTranslation } from 'react-i18next'
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium'
import upperFirst from 'lodash/upperFirst'
import { toast } from 'react-toastify'
import { isSameDay } from 'date-fns/isSameDay'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import { LoaderData, Participants, OptionsParticipants, OptionType, Events } from '../../../types'
import {
  useMembershipLevelOptions,
  useLocationDataOptions,
  useJobTitlesDataOptions,
  useParticipantOptionStatusOptions,
  useOptionsTypeOptions,
  formatDateTime,
  formatHumanTime,
  isNumberOption,
  isTextOption,
  getOption,
  Options,
} from '../../../utils'
import { adminParticipantDetailsLoader, adminEventDetailsLoader } from '../Admin.loader'
import { useDeleteOptionParticipationMutation } from '../../../services/events'
import ParticipantOptionStatusTag from '../../events/components/ParticipantOptionStatusTag'
import ParticipantStatusTag from '../../events/components/ParticipantStatusTag'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import IconButtonOutlined from '../../root/components/IconButtonOutlined'
import { StatusTag } from '../../root/components/StatusTag'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import { useContextShowStatistics } from '../Admin.Context'
import BulkEditOptionsParticipants from './components/BulkEditOptionsParticipants'
import BooleanRender from './components/BooleanRender'

const initialState: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      id: false,
    },
  },
}

interface Props {
  participant?: Participants<true>
  readOnly?: boolean
}

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
function DetailsParticipants({
  data,
  event,
}: {
  data: Participants<true> | undefined
  event: Events<true> | undefined
}) {
  const { t } = useTranslation('admin')
  const jobTitlesDataOptions = useJobTitlesDataOptions()
  const membershipLevelOptions = useMembershipLevelOptions(t)
  const theme = useTheme()

  const showStatistics = useContextShowStatistics()

  const membershipLevel = useMemo(
    () => membershipLevelOptions.find(option => option.value === data?.membership_level)?.label,
    [data?.membership_level, membershipLevelOptions]
  )

  return (
    <Stack direction="column" gap="0.2rem">
      <Stack direction="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} alignItems={['start', 'center']} gap="0.5rem">
          <Typography variant="h2">{t('Détails Participant')}</Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            href={data?.civi_link ?? ''}
            disabled={!data?.civi_link}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<LaunchIcon />}
            sx={{ width: 'fit-content', height: 'fit-content' }}
          >
            {t('Lien CiviCRM')}
          </Button>
        </Stack>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && (
        <>
          <Stack direction="column">
            <Stack direction={['column', 'row']} justifyContent="space-between">
              <Stack direction="row" alignItems="center" gap="1rem" flex="1 0">
                <h4>
                  {data?.last_name} {data?.first_name}
                </h4>
                {data?.is_contributor && (
                  <StatusTag
                    color={theme.palette.primary.main}
                    absolute={false}
                    position="top"
                    size="small"
                  >
                    <Stack direction="row" alignItems={'center'} gap="0.5rem">
                      <LocalActivityIcon sx={{ fontSize: '1.5rem' }} /> {t('Contributeur')}
                    </Stack>
                  </StatusTag>
                )}
              </Stack>

              <Stack
                direction={['column', 'row']}
                alignItems={['start', 'center']}
                gap={['0.5rem', '1rem']}
                flex="1 0"
              >
                <Stack direction="row" alignItems="center" gap="0.2rem">
                  <h6>{t('Email')} :</h6> <span>{data?.e_mail || 'NC'}</span>
                </Stack>
                <Stack direction="row" alignItems="center" gap="0.2rem">
                  <h6>{t('Téléphone')} :</h6> <span>{data?.phone_number || 'NC'}</span>
                </Stack>
              </Stack>
            </Stack>
            <Stack direction={['column', 'row']} alignItems={['start', 'center']}>
              <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
                <h6>{t('Statut inscription')} :</h6>
                <ParticipantStatusTag size="small" status={data?.status} />
              </Stack>
              <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
                <h6>{t('Paiement')} :</h6> <span>{data?.price ?? 0} €</span>
              </Stack>
            </Stack>
            <Stack direction={['column', 'row']} alignItems={['start', 'center']}>
              <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
                <h6>{t("Date d'inscription")} :</h6>
                <span>{formatDateTime(data?.created)}</span>
              </Stack>

              <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
                <h6>{t('Statut du membre')} :</h6> <span>{membershipLevel ?? 'NC'}</span>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={['column', 'row']} alignItems={['start', 'center']}>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Type de poste')} :</h6>{' '}
              <span>
                {upperFirst(
                  jobTitlesDataOptions.find(option => option.value === data?.job_title)?.label ??
                    'NC'
                )}
              </span>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Service')} :</h6>
              <span>{data?.service_name || 'NC'}</span>
            </Stack>
          </Stack>
          <Stack direction={['column', 'row']} alignItems={['start', 'center']}>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Région de rattachement')} :</h6>
              <span>{data?.home_region || 'NC'}</span>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Phase de formation')} :</h6> <span>{data?.training_phase || 'NC'}</span>
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  )
}
/* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

export default function AdminParticipantDetails({ participant, readOnly }: Props) {
  const { t } = useTranslation('admin')
  const { eventId, participantId } = useParams()
  const revalidator = useRevalidator()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')

  const showStatistics = useContextShowStatistics()

  const [open, setOpen] = useState(false)
  const [currentParticipations, setCurrentParticipations] = useState<
    Map<number, OptionsParticipants<true>>
  >(new Map())

  // Hack to get the data from the parent component if not rendered from router
  const routerData = useLoaderData() as LoaderData<ReturnType<typeof adminParticipantDetailsLoader>>
  const data = participant ?? routerData
  const { event, subEvents, sessions } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >

  const optionsTypeOptions = useOptionsTypeOptions(t)
  const locationDataOptions = useLocationDataOptions()
  const participantOptionStatusOptions = useParticipantOptionStatusOptions(t)

  const [deleteOptionParticipant] = useDeleteOptionParticipationMutation()
  const onDeleteSubEventParticipant = useCallback(
    (participationId: number) => {
      if (window.confirm(t('Êtes-vous sûr de vouloir supprimer cette option ?'))) {
        deleteOptionParticipant({
          eventId: Number(eventId),
          id: participationId,
        })
          .then(data => {
            revalidator.revalidate()
            if ('data' in data) {
              toast.success(t('Option supprimée'))
            }
          })
          .catch(() => {
            toast.error(t('Erreur lors de la suppression de la participation'))
          })
      }
    },
    [t, deleteOptionParticipant, eventId, revalidator]
  )

  type DataType = NonNullable<typeof data>['participant_options'][number]
  const columns: GridColDef<DataType>[] = useMemo(
    () => [
      ...(readOnly
        ? []
        : ([
            {
              field: 'actions',
              headerName: t('Actions'),
              type: 'actions',
              width: 100,
              filterable: false,
              disableExport: true,
              renderCell: params => (
                <Stack direction="row">
                  <Tooltip title={t('Modifier inscription complémentaire')}>
                    <IconButton
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(
                          `/admin/details/${eventId}/participants/${participantId}/option/${params.row.id}?${searchParams.toString()}`
                        )
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('Annuler inscription complémentaire')}>
                    <IconButton
                      color="error"
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        onDeleteSubEventParticipant(params.row.id)
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            },
          ] as GridColDef<DataType>[])),
      { field: 'id', headerName: t('ID'), width: 60 },
      {
        field: 'status',
        headerName: t("Statut d'inscription"),
        width: 210,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: participantOptionStatusOptions,
        renderCell: params => (
          <ParticipantOptionStatusTag
            size="small"
            status={params.value as OptionsParticipants<true>['status']}
          />
        ),
      },
      {
        field: 'type',
        headerName: t('Type'),
        width: 150,
        type: 'singleSelect',
        valueOptions: optionsTypeOptions,
        valueGetter: (_value, row) => row.option.type,
        renderCell: params => {
          const type = getOption(optionsTypeOptions, undefined, params.value as OptionType)
          return (
            <Chip
              sx={theme => ({
                backgroundColor: type?.color,
                color: theme.palette.getContrastText(type?.color ?? 'inherit'),
              })}
              size="small"
              label={type?.label ?? 'NC'}
            />
          )
        },
      },
      {
        field: 'name',
        headerName: t('Nom'),
        width: 350,
        valueGetter: (_value, row) => row.option.name,
      },
      {
        field: 'present',
        headerName: t('Présent'),
        width: 100,
        type: 'boolean',
        renderCell: BooleanRender,
      },
      {
        field: 'hotel',
        headerName: t('Hôtel'),
        width: 300,
        type: 'singleSelect',
        valueOptions: locationDataOptions,
        valueFormatter: value =>
          value
            ? (locationDataOptions.find(option => option.value === value)?.label ?? 'NC')
            : 'N/A',
      },
      {
        field: 'value',
        headerName: t('Valeur'),
        width: 150,
        valueFormatter: (value, row) =>
          isNumberOption(row.option) || isTextOption(row.option) ? value || 'NC' : 'N/A',
      },
      {
        field: 'subEvent',
        headerName: t('Intervention'),
        width: 300,
        type: 'singleSelect',
        valueOptions: subEvents?.reduce((acc, subEvent) => {
          acc.push({
            value: subEvent.id,
            label: subEvent.name,
          })
          return acc
        }, [] as Options[]),
        valueGetter: (_value, row) =>
          'sub_event' in row.option ? row.option.sub_event : undefined,
        valueFormatter: (value, row) => {
          const subEvent = subEvents?.find(el => el.id === value)
          if (row.option.type === OptionType.SubEventOption) {
            if (subEvent === undefined) return 'NC'
            return `${subEvent.name} [${formatDateTime(subEvent.start)} - ${isSameDay(subEvent.end, subEvent.start) ? formatHumanTime(subEvent.end) : formatDateTime(subEvent.end)}]`
          }
          return 'N/A'
        },
      },
      {
        field: 'session',
        headerName: t('Séance'),
        width: 300,
        type: 'singleSelect',
        valueOptions: sessions?.reduce((acc, session) => {
          acc.push({
            value: session.id,
            label: session.name,
          })
          return acc
        }, [] as Options[]),
        valueGetter: (_value, row) => ('session' in row.option ? row.option.session : undefined),
        valueFormatter: (value, row) => {
          const session = sessions?.find(el => el.id === value)
          if (row.option.type === OptionType.SessionOption) {
            if (session === undefined) return 'NC'
            return `${session.name} [${formatDateTime(session.start)} - ${isSameDay(session.end, session.start) ? formatHumanTime(session.end) : formatDateTime(session.end)}]`
          }
          return 'N/A'
        },
      },
    ],
    [
      readOnly,
      t,
      participantOptionStatusOptions,
      optionsTypeOptions,
      locationDataOptions,
      subEvents,
      sessions,
      navigate,
      eventId,
      participantId,
      searchParams,
      onDeleteSubEventParticipant,
    ]
  ) satisfies GridColDef<DataType>[]

  const ResponsiveButton = isMobile ? IconButtonOutlined : Button

  return (
    <>
      <Stack
        sx={theme => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: '0.5rem',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.boxShadows.main,
        })}
        padding="0.5rem"
        gap="1rem"
      >
        <DetailsParticipants data={data} event={event} />
        <Divider />
        <Stack
          direction={['column', 'row']}
          alignItems={['start', 'center']}
          justifyContent="space-between"
          gap="1rem"
        >
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <h3>{t('Liste des options')} :</h3>
            {!readOnly && (
              <ResponsiveButton
                startIcon={<AddBoxIcon />}
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate(
                    `/admin/details/${eventId}/participants/${participantId}/option/new?${searchParams.toString()}`
                  )
                }}
              >
                {isMobile ? <AddBoxIcon /> : t('Option')}
              </ResponsiveButton>
            )}
          </Stack>
          {readOnly ? (
            <div />
          ) : (
            <Stack direction="row" gap="0.5rem">
              <ResponsiveButton
                startIcon={<ArrowCircleLeftIcon />}
                onClick={() => {
                  navigate(`/admin/details/${eventId}/participants`)
                }}
              >
                {isMobile ? <ArrowCircleLeftIcon /> : t('Retour participants')}
              </ResponsiveButton>
              <ResponsiveButton
                startIcon={<EmailIcon />}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  const subject = `SFCTCV Événement ${event?.name} - Information sur votre réservation`
                  const body = `Bonjour ${data?.first_name} ${data?.last_name},%0D%0A%0D%0ANous venons de mettre à jour votre réservation pour l'événement ${event?.name}.%0D%0A%0D%0ACordialement,%0D%0AL'équipe SFCTCV`

                  window.open(`mailto:${data?.e_mail}?subject=${subject}&body=${body}"`, '_blank')
                }}
              >
                {isMobile ? <EmailIcon /> : t('Envoyer email')}
              </ResponsiveButton>
            </Stack>
          )}
        </Stack>
        <Stack
          sx={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
            overflow: 'auto',
          }}
        >
          <StripedDataGrid
            rows={data?.participant_options ?? []}
            columns={columns}
            count={data?.participant_options?.length}
            paginationMode="client"
            initialState={initialState}
            checkboxSelection={!readOnly}
            editOptions={
              readOnly
                ? undefined
                : {
                    onClick: selectedRows => {
                      setOpen(true)
                      setCurrentParticipations(
                        selectedRows as Map<number, OptionsParticipants<true>>
                      )
                    },
                  }
            }
          />
        </Stack>
      </Stack>

      <BulkEditOptionsParticipants
        open={open}
        setOpen={setOpen}
        currentParticipations={currentParticipations}
        name={data ? `${data.last_name} ${data.first_name}` : ''}
        optionType={undefined}
        subEventType={undefined}
        isParticipantView
      />

      <Outlet />
    </>
  )
}
